"use client";
import React, { useId } from "react";
import ValuesChips from "./ValuesChips";

type Item = {
  id: string;
  number: string;
  title: string;
  content: React.ReactNode;
  chipRows?: { label: string; icon?: string }[][];
};

type AccordionProps = {
  items: Item[];
  openId: string | null;
  setOpenId: React.Dispatch<React.SetStateAction<string | null>>;
};

export default function WhatShapeAccordion({
  items,
  openId,
  setOpenId,
}: AccordionProps) {
  return (
    <div className="flex flex-col gap-[5px] md:gap-[20px]">
      {items?.map((item, index) => (
        <AccordionRow
          key={item.id}
          item={item}
          open={openId === item.id}
          onToggle={() =>
            setOpenId((prev) => (prev === item.id ? null : item.id))
          }
          index={index !== items.length - 1}
        />
      ))}
    </div>
  );
}

type RowProps = {
  item: Item;
  open: boolean;
  onToggle: () => void;
  index: boolean;
};

function AccordionRow({ item, open, onToggle, index }: RowProps) {
  const contentId = useId();
  return (
    <div className="flex lg:gap-[30px] gap-[10px]">
      <div className="w-[50px] flex flex-col items-center gap-[10px]">
        <div className={open ? "text-[#225bd4]" : "text-[#636364]"}>
          <span className="[font-family:'Clash Display'] font-medium text-[20px] leading-[120%]">
            {item.number}
          </span>
        </div>
        {index && (
          <div className="flex-1 2xl:bg-[#ecf0f6] w-[4px] min-h-[20px]" />
        )}
      </div>

      <div className="flex-1">
        <button
          aria-expanded={open}
          aria-controls={contentId}
          aria-label="toggle-button"
          onClick={onToggle}
          className="w-full text-left cursor-pointer"
        >
          <div className="clash-font text-[clamp(1.125rem,0.6136rem+2.2727vw,2.375rem)] leading-[90%] flex items-center justify-between">
            <span
              className={
                open
                  ? "text-[#00040b] font-medium"
                  : "text-[#636364] font-normal"
              }
            >
              {item.title}
            </span>
            <span className="ml-4 text-[clamp(1.25rem,0.4186rem+1.7321vw,3.125rem)] text-[#636364]">
              {open ? "−" : "+"}
            </span>
          </div>
        </button>
        <div
          id={contentId}
          role="region"
          aria-hidden={!open}
          className={
            (open ? "mt-[15px] " : "mt-0 ") +
            "grid overflow-hidden transition-[grid-template-rows] duration-300 ease-out " +
            (open ? "grid-rows-[1fr]" : "grid-rows-[0fr]")
          }
        >
          <div className="min-h-0 overflow-hidden">
            <div className="text-[#00040b] text-[clamp(0.875rem,0.6705rem+0.9091vw,1.375rem)] leading-[160%]">
              {item.content}
              {Array.isArray(item.chipRows) && item.chipRows.length > 0 ? (
                <ValuesChips rows={item.chipRows} />
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
